'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole = 'customer' | 'rider' | 'admin' | 'super_admin';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  phone_number: string | null;
  role: UserRole;
  status: 'active' | 'suspended' | 'pending_verification';
  avatar_url: string | null;
  phone_verified: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  isCustomer: () => boolean;
  isRider: () => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  getRedirectPath: () => string;
  sendPhoneOtp: (phoneNumber: string) => Promise<void>;
  verifyPhoneOtp: (phoneNumber: string, otpCode: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) return null;
      return data as UserProfile;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      }
      setLoading(false);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up — accepts role and other metadata
  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : process.env.NEXT_PUBLIC_SITE_URL || '';
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata.full_name || metadata.fullName || '',
          phone_number: metadata.phone_number || metadata.phoneNumber || '',
          role: metadata.role || 'customer',
          avatar_url: metadata.avatar_url || metadata.avatarUrl || ''
        },
        emailRedirectTo: `${origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  // Role helpers — use profile role (most accurate) with fallback to metadata
  const getRole = (): UserRole => {
    return profile?.role || (user?.user_metadata?.role as UserRole) || 'customer';
  };

  const isCustomer = () => getRole() === 'customer';
  const isRider = () => getRole() === 'rider';
  const isAdmin = () => getRole() === 'admin' || getRole() === 'super_admin';
  const isSuperAdmin = () => getRole() === 'super_admin';

  // Returns the default redirect path based on role
  const getRedirectPath = (): string => {
    const role = getRole();
    if (role === 'admin' || role === 'super_admin') return '/admin-dashboard';
    if (role === 'rider') return '/rider-dashboard';
    return '/';
  };

  // Send Phone OTP (generates OTP via DB function and returns it for display/SMS)
  const sendPhoneOtp = async (phoneNumber: string): Promise<void> => {
    if (!user) throw new Error('Must be logged in to verify phone');
    const { error } = await supabase.rpc('generate_phone_otp', {
      p_user_id: user.id,
      p_phone_number: phoneNumber,
      p_purpose: 'phone_verification'
    });
    if (error) throw error;
  };

  // Verify Phone OTP
  const verifyPhoneOtp = async (phoneNumber: string, otpCode: string): Promise<boolean> => {
    if (!user) throw new Error('Must be logged in to verify phone');
    const { data, error } = await supabase.rpc('verify_phone_otp', {
      p_user_id: user.id,
      p_phone_number: phoneNumber,
      p_otp_code: otpCode,
      p_purpose: 'phone_verification'
    });
    if (error) throw error;
    if (data) {
      // Refresh profile to reflect phone_verified = true
      const p = await fetchProfile(user.id);
      setProfile(p);
    }
    return !!data;
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    isCustomer,
    isRider,
    isAdmin,
    isSuperAdmin,
    getRedirectPath,
    sendPhoneOtp,
    verifyPhoneOtp
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
